import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaFilter, FaSort, FaTh, FaList, FaSearch, FaTimes } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiOutlineDocumentDuplicate } from "react-icons/hi2";
import SortDropdown from "./SortDropdown";
import { ArrowDownUp, ListFilter, ChevronDown, ChevronUp } from "lucide-react";
import TableSkeleton from "./TableSkelaton";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "./Loader";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
const ReusableTable = ({
  columns,
  data,
  onSearch,
  onDelete,
  onDuplicate,
  gridView,
  isLoading = false,
  onSort,
  onFilter,
  // Server-side pagination props
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  useServerPagination = false,
  filterColumn,
  setFilterColumn,
  filterValue,
  setFilterValue,
  filterOptions,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState({});
  const [isGridView, setIsGridView] = useState(gridView);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filterValue || "");
  const navigate = useNavigate();

  const capitalizeFirstLetter = (string) => {
    return string
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const capitalizedColumns = columns.map(column => ({
    ...column,
    header: typeof column.header === 'string' 
      ? capitalizeFirstLetter(column.header)
      : column.header
  }));

  const table = useReactTable({
    data,
    columns: capitalizedColumns,
    state: {
      rowSelection: selectedRows,
    },
    onRowSelectionChange: (updater) => {
      const updatedSelection = updater instanceof Function ? updater(selectedRows) : updater;
      setSelectedRows(updatedSelection);
    },
    getRowId: (row) => row.id || row.name,
    getCoreRowModel: getCoreRowModel(),
    // Only use client-side pagination if not using server-side
    ...(!useServerPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectedRowCount = Object.keys(table.getState().rowSelection).length;

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const clearFilter = () => {
    setFilterValue('');
    setFilterColumn('');
    if (onFilter) {
      onFilter('', '');
    }
  };

  const handleSort = (sortOption) => {
    if (onSort) {
      onSort(sortOption);
    }
    setSortModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortModalOpen && !event.target.closest('.sort-menu') && 
          !event.target.closest('.btn-light')) {
        setSortModalOpen(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortModalOpen]);

  // Handle filter value changes
  useEffect(() => {
    if (filterColumn) {
      // Trigger filter callback if provided
      if (typeof onFilter === 'function') {
        onFilter(filterColumn, filterValue || '');
      }
    }
  }, [filterColumn, filterValue, onFilter]);

  // Calculate pagination values
  const isServerPagination = useServerPagination && page !== undefined && perPage !== undefined && total !== undefined;
  
  // For client-side pagination, get values from table state
  const tablePagination = table.getState().pagination;
  const clientPageIndex = tablePagination.pageIndex;
  const clientPageSize = tablePagination.pageSize;
  const clientTotal = data.length;
  
  // For server-side pagination, use provided props (page should be 1-based)
  const serverCurrentPage = page ?? 1;
  const serverPerPage = perPage ?? 10;
  const serverTotal = total ?? 0;
  
  // Determine which values to use
  const currentPage = isServerPagination ? serverCurrentPage : clientPageIndex + 1; // Display as 1-based
  const pageSize = isServerPagination ? serverPerPage : clientPageSize;
  const totalCount = isServerPagination ? serverTotal : clientTotal;
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
  
  // Calculate display range
  const startItem = totalCount > 0 && pageSize > 0 
    ? (isServerPagination ? (serverCurrentPage - 1) * serverPerPage + 1 : clientPageIndex * clientPageSize + 1)
    : 0;
  const endItem = totalCount > 0 && pageSize > 0
    ? Math.min(isServerPagination ? serverCurrentPage * serverPerPage : (clientPageIndex + 1) * clientPageSize, totalCount)
    : 0;

  const columnOptions = filterOptions || (window.location.pathname === "/skills/email" ? [
    { value: "tone", label: "Tone" },
    { value: "occasion", label: "Occasion" },
    { value: "further_details", label: "Further Details" },
    { value: "created_at", label: "Created At" }
  ] : []);

  return (
    <div className="table-container">
      <div className="table-toolbar d-flex justify-content-between align-items-center mb-3">
        <div className="toolbar-left d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <div className="search-input-container" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Document Name"
              value={searchValue}
              onChange={handleSearch}
              className="search-input"
              style={{ backgroundColor: '#f0f0f0', paddingRight: '30px' }}
            />
            <FaSearch style={{ 
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666'
            }} />
          </div>
          {filterColumn && (
            <div className="filter-input-container" style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder={`Filter by ${columnOptions.find(opt => opt.value === filterColumn)?.label || filterColumn}`}
                value={filterValue || ''}
                onChange={(e) => setFilterValue(e.target.value)}
                className="form-control"
                style={{ minWidth: '200px', paddingRight: '30px' }}
              />
              {filterValue && (
                <FaTimes
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#666',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onClick={clearFilter}
                />
              )}
            </div>
          )}
        </div>
        <div className="toolbar-right d-flex gap-2">
          <div className="filter-container" style={{ position: 'relative' }}>
            <button 
              className="btn btn-light"
              onClick={() => setShowFilter(!showFilter)}
            >
              <ListFilter /> Filter
            </button>
            {showFilter && (
              <div className="filter-dropdown" style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '10px',
                zIndex: 1000,
                minWidth: '200px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {columnOptions.map(option => (
                  <div 
                    key={option.value}
                    className="filter-option"
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: filterColumn === option.value ? '#f5f5f5' : '',
                      fontWeight: filterColumn === option.value ? 'bold' : 'normal'
                      
                    }}
                    onClick={() => {
                      setFilterColumn(option.value);
                      if (!filterValue) {
                        setFilterValue('');
                      }
                      setShowFilter(false);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="sort-button-container">
            <button
              className="btn btn-light"
              onClick={() => setSortModalOpen(!sortModalOpen)}
            >
              <ArrowDownUp /> Sort
            </button>
            <SortDropdown 
              isOpen={sortModalOpen}
              onSort={handleSort}
              onClose={() => setSortModalOpen(false)}
            />
          </div>
          
          <div className="btn-group" role="group" aria-label="View toggle">
            <button
              type="button"
              className={`btn ${!isGridView ? 'btn-secondary' : 'btn-light'}`}
              onClick={() => setIsGridView(false)}
            >
              <FaList />
            </button>
            <button
              type="button"
              className={`btn ${isGridView ? 'btn-secondary' : 'btn-light'}`}
              onClick={() => setIsGridView(true)}
            >
              <FaTh />
            </button>
          </div>
        </div>
      </div>

      {selectedRowCount > 0 && (
        <div className="action-buttons mb-1">
          <span className="mt-2">
            <span className="fw-bold">{selectedRowCount}</span>
            <span> selected </span>
          </span>
          <button
            className="btn btn-outline-secondary"
            onClick={() => onDuplicate(Object.keys(selectedRows))}
          >
            <HiOutlineDocumentDuplicate className="mb-1" />
            &nbsp; Duplicate
          </button>
          <button
            className="btn btn-outline-danger"
            onClick={() => onDelete(Object.keys(selectedRows))}
          >
            <RiDeleteBin6Line className="mb-1" /> &nbsp; Delete
          </button>
        </div>
      )}

      <hr />

      {isLoading ? (
        <TableSkeleton isGridView={isGridView} columns={columns.length} rows={5} />
      ) : !isGridView ? (
        <div className="">
        <table className="table custom-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    cell.column.columnDef.header === "Name" || cell.column.columnDef.header === "Tone" || cell.column.columnDef.header === "Occasion" || cell.column.columnDef.header === "Further Details" ? (
                      <td key={cell.id} className="table-cell text-wrap" >
                        <OverlayTrigger
                          placement="left"
                        overlay={<Tooltip>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Tooltip>}
                      >
                        <span>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                      </OverlayTrigger>
                    </td>
                    ) : (
                      <td key={cell.id} className="table-cell position-relative" >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      ) : (
        <div className="grid-view">
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <div key={row.id} className="grid-item">
                <div className="grid-item-header">
                  <h5 className="mb-0">{row.original.name || 'Item'}</h5>
                  <input
                    type="checkbox"
                    className="grid-item-checkbox form-check-input"
                    checked={!!selectedRows[row.id]}
                    onChange={() =>
                      setSelectedRows((prev) => ({
                        ...prev,
                        [row.id]: !prev[row.id],
                      }))
                    }
                  />
                </div>
                <div className="grid-item-content">
                  {row.getVisibleCells().map((cell) => (
                    <div key={cell.id} className="grid-item-field">
                      <span className="grid-item-label">
                        {typeof cell.column.columnDef.header === 'string'
                          ? capitalizeFirstLetter(cell.column.columnDef.header)
                          : cell.column.columnDef.header}
                      </span>
                      <span className="grid-item-value">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center">No data available</div>
          )}
        </div>
      )}

      <hr />
      <div className="pagination d-flex">
        <div className="rows-per-page">
          <span>Show rows:</span>
          <select
            className="rows-dropdown"
            value={pageSize}
            onChange={(e) => {
              const newValue = Number(e.target.value);
              if (isServerPagination && onPerPageChange) {
                onPerPageChange(newValue);
              } else {
                table.setPageSize(newValue);
              }
            }}
          >
            {[5 ,10 , 15, 25, 300].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="pagination-info">
          {startItem}-{endItem} of {totalCount}
        </div>

        <div className="pagination-buttons">
          <button
            className="btn"
            onClick={() => {
              if (isServerPagination && onPageChange) {
                onPageChange(serverCurrentPage - 1);
              } else {
                table.previousPage();
              }
            }}
            disabled={isServerPagination ? serverCurrentPage <= 1 : !table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="btn"
            onClick={() => {
              if (isServerPagination && onPageChange) {
                onPageChange(serverCurrentPage + 1);
              } else {
                table.nextPage();
              }
            }}
            disabled={isServerPagination ? serverCurrentPage >= totalPages : !table.getCanNextPage()}
          >
            {">"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReusableTable;